import type { CompartilhamentoDaVisao, ConfigDaVisao, PapelNaVisao, Visao, VisibilidadeDaVisao } from '@orbind/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiBase, webTokenStorage } from '../../lib/api.ts'

/**
 * Visões de uma tela (SPEC-20261103-1418): de um quadro (`quadroId`) ou da
 * lista de Itens de Trabalho (`null`). Mesmo hook, mesmas regras.
 */

export type NovaVisao = {
  id?: string
  name: string
  visibility: VisibilidadeDaVisao
  publicRole: PapelNaVisao
  shares: CompartilhamentoDaVisao[]
  config: ConfigDaVisao
}

export type AlteracaoDaVisao = Partial<Omit<NovaVisao, 'id' | 'config'>> & { config?: Partial<ConfigDaVisao> }

export const chaveDasVisoes = (quadroId: string | null) => ['views', quadroId ?? 'itens'] as const

type Corpo = { view?: Visao; views?: Visao[]; error?: { message?: string } } | null

async function ler(r: Response, falha: string) {
  const corpo = (await r.json().catch(() => null)) as Corpo
  if (!r.ok || !corpo) throw new Error(corpo?.error?.message ?? falha)
  return corpo
}

export function useVisoes(quadroId: string | null) {
  return useQuery({
    queryKey: chaveDasVisoes(quadroId),
    queryFn: async (): Promise<VisaoNaTela[]> => {
      const r = quadroId
        ? await api.kanbans[':id'].views.$get({ param: { id: quadroId } })
        : await api['work-items'].views.$get()
      return ((await ler(r as unknown as Response, 'Não foi possível carregar as visões.')).views ?? []).map(comRascunho)
    },
  })
}

/**
 * A visão como a pessoa a vê (SPEC-20261104-2147): o rascunho dela por cima
 * da configuração salva. `salva` guarda a original, para o ✗ e para comparar.
 */
export type VisaoNaTela = Visao & { salva: Visao['config']; emEdicao: boolean }

export function comRascunho(v: Visao): VisaoNaTela {
  const draft = v.draft ?? null
  return {
    ...v,
    salva: v.config,
    config: draft ? { ...v.config, ...draft } : v.config,
    emEdicao: Boolean(draft && Object.keys(draft).length > 0),
  }
}

export function useMutacoesDeVisao(quadroId: string | null) {
  const queryClient = useQueryClient()
  const chave = chaveDasVisoes(quadroId)
  /** Troca (ou põe) a visão na lista em cache, sem esperar a releitura. */
  const guardar = (bruta: Visao) => {
    const visao = comRascunho(bruta)
    queryClient.setQueryData<VisaoNaTela[]>(chave, (lista) => {
      if (!lista) return [visao]
      return lista.some((v) => v.id === visao.id) ? lista.map((v) => (v.id === visao.id ? visao : v)) : [...lista, visao]
    })
  }

  const criar = useMutation({
    mutationFn: async (input: NovaVisao): Promise<Visao> => {
      const r = quadroId
        ? await api.kanbans[':id'].views.$post({ param: { id: quadroId }, json: input as never })
        : await api['work-items'].views.$post({ json: input as never })
      return (await ler(r as unknown as Response, 'Não foi possível criar a visão.')).view!
    },
    onSuccess: guardar,
  })

  const alterar = useMutation({
    mutationFn: async ({ id, ...input }: AlteracaoDaVisao & { id: string }): Promise<Visao> => {
      const r = quadroId
        ? await api.kanbans[':id'].views[':viewId'].$put({ param: { id: quadroId, viewId: id }, json: input as never })
        : await api['work-items'].views[':viewId'].$put({ param: { viewId: id }, json: input as never })
      return (await ler(r as unknown as Response, 'Não foi possível salvar a visão.')).view!
    },
    // na tela já: coluna arrastada ou tipo trocado não podem piscar de volta
    onMutate: ({ id, config, ...resto }) => {
      queryClient.setQueryData<VisaoNaTela[]>(chave, (lista) =>
        lista?.map((v) => (v.id === id ? { ...v, ...resto, config: { ...v.config, ...config } } : v)),
      )
    },
    onSuccess: guardar,
    onError: () => void queryClient.invalidateQueries({ queryKey: chave }),
  })

  const apagar = useMutation({
    mutationFn: async (id: string) => {
      const r = quadroId
        ? await api.kanbans[':id'].views[':viewId'].$delete({ param: { id: quadroId, viewId: id } })
        : await api['work-items'].views[':viewId'].$delete({ param: { viewId: id } })
      await ler(r as unknown as Response, 'Não foi possível apagar a visão.')
      return id
    },
    onSuccess: (id) => queryClient.setQueryData<Visao[]>(chave, (lista) => lista?.filter((v) => v.id !== id)),
  })

  /** Base das rotas de rascunho desta tela. */
  const rotaDoRascunho = (id: string, sufixo = '') =>
    `${apiBase()}${quadroId ? `/kanbans/${quadroId}` : '/work-items'}/views/${id}/draft${sufixo}`
  const chamar = async (url: string, metodo: string, corpo?: unknown) => {
    const r = await fetch(url, {
      method: metodo,
      headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}`, ...(corpo ? { 'content-type': 'application/json' } : {}) },
      body: corpo ? JSON.stringify(corpo) : undefined,
    })
    return (await ler(r, 'Não foi possível salvar a edição da visão.')).view!
  }

  /**
   * Mexer na TELA com uma visão escolhida (SPEC-20261104-2147): grava no
   * rascunho da pessoa, não na visão. Aparece na hora; o servidor guarda
   * para o próximo login.
   */
  const rascunhar = useMutation({
    mutationFn: ({ id, config }: { id: string; config: Partial<ConfigDaVisao> }) =>
      chamar(rotaDoRascunho(id), 'PUT', { config }),
    onMutate: ({ id, config }) => {
      queryClient.setQueryData<VisaoNaTela[]>(chave, (lista) =>
        lista?.map((v) =>
          v.id === id
            ? { ...v, draft: { ...(v.draft ?? {}), ...config }, config: { ...v.config, ...config }, emEdicao: true }
            : v,
        ),
      )
    },
    onSuccess: guardar,
    onError: () => void queryClient.invalidateQueries({ queryKey: chave }),
  })

  /** ✗: volta ao que estava salvo. */
  const descartar = useMutation({
    mutationFn: (id: string) => chamar(rotaDoRascunho(id), 'DELETE'),
    onSuccess: guardar,
  })

  /** ✓: o rascunho vira a visão (para todos que a veem). */
  const confirmar = useMutation({
    mutationFn: (id: string) => chamar(rotaDoRascunho(id, '/commit'), 'POST'),
    onSuccess: guardar,
  })

  return { criar, alterar, apagar, rascunhar, descartar, confirmar }
}
